// src/components/TranslationProvider.tsx
import { useEffect, useState } from "react";
import { IntlProvider } from "react-intl";

// src/messages-observer.ts
var MessagesObserver = class {
  subscriber = null;
  messages = {};
  subscribe(callback) {
    this.subscriber = callback;
    this.subscriber(this.messages);
  }
  notify(newMessages) {
    this.messages = Object.assign(this.messages, newMessages);
    if (this.subscriber)
      this.subscriber(this.messages);
  }
};
var messagesSubject = new MessagesObserver();

// src/helpers/get-translation-messages-by-locale.ts
import { Locale } from "@scouts/helpers";
var getTranslationMessagesByLocale = (messages, locale) => {
  const localeMessages = {};
  const localeIndex = {
    [Locale.GB]: 0,
    [Locale.ES]: 1
  };
  Object.entries(messages).forEach(([id, message]) => {
    localeMessages[id] = message[localeIndex[locale]] ?? message[0] ?? "";
  });
  return localeMessages;
};

// src/components/TranslationProvider.tsx
import { jsx } from "react/jsx-runtime";
var TranslationProvider = ({ children, locale }) => {
  const [messages, setMessages] = useState({});
  useEffect(() => {
    messagesSubject.subscribe((newMessages) => {
      setMessages(getTranslationMessagesByLocale(newMessages, locale));
    });
  }, [locale]);
  if (Object.keys(messages).length === 0)
    return null;
  return /* @__PURE__ */ jsx(IntlProvider, { locale, messages, children });
};

// src/hooks/use-translation.ts
import { useCallback } from "react";
import { useIntl as useIntl2 } from "react-intl";
import { Locale as Locale3 } from "@scouts/helpers";

// src/hooks/use-locale.ts
import { useIntl } from "react-intl";
import { Locale as Locale2 } from "@scouts/helpers";
var isLocale = (locale) => Object.values(Locale2).includes(locale);
var useLocale = () => {
  const { locale } = useIntl();
  if (!isLocale(locale)) {
    throw new Error(`Locale ${locale} is not supported`);
  }
  return locale;
};

// src/hooks/use-translation.ts
var createUseTranslation = (prefix) => () => {
  const { formatMessage } = useIntl2();
  const locale = useLocale();
  return useCallback(
    (arg1, values) => {
      if (Array.isArray(arg1)) {
        return arg1[locale === Locale3.ES ? 1 : 0];
      }
      const fullId = `${prefix}${arg1.id}`;
      const formattedMessage = formatMessage({ id: fullId }, values);
      if (typeof formattedMessage !== "string")
        throw new Error(
          `Message of translation key ${fullId} returned ${typeof formattedMessage} instead of string`
        );
      return formattedMessage;
    },
    [formatMessage, locale]
  );
};

// src/components/Translation.tsx
import { FormattedMessage } from "react-intl";
import { Locale as Locale4 } from "@scouts/helpers";
import { Fragment, jsx as jsx2 } from "react/jsx-runtime";
var createTranslation = (prefix) => ({ id, strings, values }) => {
  const locale = useLocale();
  if (strings) {
    return /* @__PURE__ */ jsx2(Fragment, { children: strings[locale === Locale4.ES ? 1 : 0] });
  }
  const fullId = `${prefix}${id}`;
  return /* @__PURE__ */ jsx2(FormattedMessage, { id: fullId, values });
};

// src/create-translation-with-messages.ts
var createTranslationWithMessages = (messages, prefix) => {
  const messagesWithPrefix = Object.fromEntries(
    Object.entries(messages).map(([key, value]) => [`${prefix}${key}`, value])
  );
  messagesSubject.notify(messagesWithPrefix);
  return {
    Translation: createTranslation(prefix),
    useTranslation: createUseTranslation(prefix)
  };
};
export {
  TranslationProvider,
  createTranslationWithMessages,
  getTranslationMessagesByLocale,
  useLocale
};
//# sourceMappingURL=index.js.map